import React, {useState} from 'react'
import styled from 'styled-components';
import Button from '../components/Button';
import AIDemo from '../components/AIDemo';
import SEO from '../components/SEO';
import theme from '../theme';

interface Prop{
  history?: any
}

const features = [
  { title: "Trained on your own content", text: "Point it at your website and it learns your pricing, policies, and product details - no manual training data to write." },
  { title: "Answers from facts, not guesses", text: "Every answer is grounded in your actual content. If it doesn't know, it says so instead of making something up." },
  { title: "Live on your site in minutes", text: "Add one snippet of code, the same way you'd add Google Analytics. No developer required." },
  { title: "Stays current automatically", text: "Update your content and the chatbot's answers update with it - no retraining, no waiting." },
]

const steps = [
  { title: "1. Point it at your website", text: "We read your pages the way a new employee would, and turn them into a knowledge base for your chatbot." },
  { title: "2. Review and refine", text: "Check what it learned, edit anything you want to adjust, and add anything that's missing." },
  { title: "3. Add it to your site", text: "Paste one script tag and your customers can start asking questions immediately." },
]

const Home : React.FC<Prop>=(prop)=>{
  const [ show, setshow ] = useState(false)

  const goToRegister = () => {
    if (prop.history) prop.history.push("/register")
  }

  const organizationJsonLd = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "MiAmigo.AI",
    "applicationCategory": "BusinessApplication",
    "operatingSystem": "Web",
    "description": "AI chatbot that answers customer questions using a business's own website content, installable on any site in minutes.",
    "offers": {
      "@type": "AggregateOffer",
      "lowPrice": "0",
      "highPrice": "20",
      "priceCurrency": "USD",
    },
  }

  return(
    <>
      <SEO
        title="MiAmigo.AI - AI Chatbot Trained on Your Website"
        description="Add an AI chatbot to your website that answers customer questions using your own content. No coding, no training data, live in minutes."
        path="/"
        jsonLd={organizationJsonLd}
      />
      <MainContainer>
        <HeroSection>
          <Header>An AI chatbot that actually knows your business</Header>
          <SubHeader>MiAmigo.ai reads your website and answers your customers' questions from it - accurately, 24/7, with no coding and no training data to write.</SubHeader>
          <Row>
            <Button text="Launch Demo" handleClick={()=>setshow(true)}/>
            <Button text="Get Chatbot" handleClick={goToRegister}/>
          </Row>
        </HeroSection>

        <Section>
          <SectionHeader>Built to answer, not to guess</SectionHeader>
          <FeatureGrid>
            {features.map((f) => (
              <FeatureCard key={f.title}>
                <FeatureTitle>{f.title}</FeatureTitle>
                <FeatureText>{f.text}</FeatureText>
              </FeatureCard>
            ))}
          </FeatureGrid>
        </Section>

        <Section>
          <SectionHeader>How it works</SectionHeader>
          <StepsRow>
            {steps.map((s) => (
              <StepCard key={s.title}>
                <StepTitle>{s.title}</StepTitle>
                <StepText>{s.text}</StepText>
              </StepCard>
            ))}
          </StepsRow>
        </Section>

        <CTASection>
          <CTAHeader>See it answer questions in real time</CTAHeader>
          <Row>
            <Button text="Launch Demo" handleClick={()=>setshow(true)}/>
            <Button text="Get Chatbot" handleClick={goToRegister}/>
          </Row>
        </CTASection>

        {show?
          <AIDemo setshow={setshow}/>:<></>
        }
      </MainContainer>
    </>
  );
}

export const MainContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`

const HeroSection = styled.div`
  padding: 60px 25px 40px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  color: ${theme.colors.text};
  max-width: 800px;
  @media (max-width: 770px) {
    padding: 40px 25px 30px;
  }
`

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
`

const Header = styled.h1`
  color: ${theme.colors.text};
  font-size: 52px;
  font-weight: 900;
  padding: 10px;
  margin: 0;
  text-align: center;
  line-height: 1.15;
  @media (max-width: 770px) {
    font-size: 2.2rem;
  }
`;

const SubHeader = styled.p`
  font-size: 18px;
  color: ${theme.colors.textMuted};
  max-width: 620px;
  line-height: 1.6;
  margin: 10px 0 20px 0;
`

const Section = styled.div`
  width: 100%;
  max-width: 1000px;
  padding: 40px 25px;
  display: flex;
  flex-direction: column;
  align-items: center;
`

const SectionHeader = styled.h2`
  font-size: 28px;
  font-weight: 800;
  color: ${theme.colors.text};
  margin-bottom: 28px;
  text-align: center;
`

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 20px;
  width: 100%;
  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`

const FeatureCard = styled.div`
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radius.lg};
  box-shadow: ${theme.shadow.card};
  padding: 22px;
`

const FeatureTitle = styled.div`
  font-size: 17px;
  font-weight: 700;
  color: ${theme.colors.text};
  margin-bottom: 8px;
`

const FeatureText = styled.div`
  font-size: 14px;
  color: ${theme.colors.textMuted};
  line-height: 1.6;
`

const StepsRow = styled.div`
  display: flex;
  gap: 20px;
  width: 100%;
  flex-wrap: wrap;
  justify-content: center;
`

const StepCard = styled.div`
  flex: 1;
  min-width: 220px;
  max-width: 300px;
  text-align: center;
  padding: 10px;
`

const StepTitle = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: ${theme.colors.primary};
  margin-bottom: 8px;
`

const StepText = styled.div`
  font-size: 14px;
  color: ${theme.colors.textMuted};
  line-height: 1.6;
`

const CTASection = styled.div`
  width: 100%;
  max-width: 700px;
  padding: 40px 25px 60px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`

const CTAHeader = styled.h2`
  font-size: 24px;
  font-weight: 800;
  color: ${theme.colors.text};
  margin-bottom: 16px;
`

export default Home
